/**
 * v-debounce 防抖指令（优化版）
 *
 * @description 防止事件频繁触发，默认采用立即执行模式（第一次立即执行，后续防抖）
 *
 * @param {number} arg - 延迟时间（毫秒），默认300ms
 * @param {Object} modifiers - 修饰符配置
 * @param {Function} value - 要执行的处理函数
 *
 * @example
 * <!-- ===== 基础用法 ===== -->
 * <!-- 点击防抖：默认300ms，立即执行模式 -->
 * <button v-debounce="handleClick">点击</button>
 *
 * @example
 * <!-- ===== 延迟时间配置 ===== -->
 * <!-- 自定义延迟：500ms防抖 -->
 * <button v-debounce:500="handleClick">点击</button>
 *
 * @example
 * <!-- ===== 执行模式 ===== -->
 * <!-- 立即执行模式（默认）：第一次立即执行，后续防抖 -->
 * <button v-debounce="handleClick">立即执行模式</button>
 *
 * <!-- 延迟执行模式：所有触发都延迟执行（适合输入框建议） -->
 * <button v-debounce.defer="handleClick">延迟执行模式</button>
 * <input v-debounce:500.input.defer="handleInput" placeholder="输入防抖" />
 *
 * @example
 * <!-- ===== 事件类型 ===== -->
 * <!-- 点击事件（默认） -->
 * <button v-debounce.click="handleClick">点击</button>
 *
 * <!-- 回车事件 -->
 * <input v-debounce.enter="handleSearch" placeholder="回车搜索" />
 *
 * <!-- 输入事件 -->
 * <input v-debounce.input="handleInput" placeholder="输入防抖" />
 *
 * <!-- 变化事件 -->
 * <select v-debounce.change="handleSelect">
 *   <option value="1">选项1</option>
 * </select>
 *
 * <!-- 提交事件 -->
 * <form v-debounce.submit="handleSubmit">
 *   <button type="submit">提交</button>
 * </form>
 *
 * <!-- 滚动事件（性能优化） -->
 * <div v-debounce.scroll="handleScroll" style="height: 200px; overflow: auto">
 *   滚动内容...
 * </div>
 *
 * <!-- 窗口调整事件（性能优化） -->
 * <div v-debounce.resize="handleResize">窗口大小改变时触发</div>
 *
 * @example
 * <!-- ===== 组合使用 ===== -->
 * <!-- 500ms延迟 + 回车事件 + 立即执行（默认） -->
 * <input v-debounce:500.enter="handleSearch" placeholder="回车搜索" />
 *
 * <!-- 300ms延迟 + 输入事件 + 延迟执行（适合实时搜索建议） -->
 * <input v-debounce:300.input.defer="handleInput" placeholder="输入建议" />
 *
 * <!-- 800ms延迟 + 变化事件 + 延迟执行（适合下拉框） -->
 * <select v-debounce:800.change.defer="handleSelect">
 *   <option value="1">选项1</option>
 * </select>
 *
 * <!-- 滚动事件 + 100ms延迟 + 立即执行（适合懒加载） -->
 * <div v-debounce:100.scroll="handleLazyLoad">懒加载区域</div>
 *
 * @example
 * <!-- ===== 性能优化场景 ===== -->
 * <!-- 滚动加载：100ms防抖，避免频繁触发 -->
 * <div
 *   v-debounce:100.scroll="handleScroll"
 *   style="height: 400px; overflow: auto"
 * >
 *   <div v-for="item in list" :key="item.id">{{ item.content }}</div>
 * </div>
 *
 * <!-- 搜索建议：300ms延迟执行，减少请求次数 -->
 * <input
 *   v-debounce:300.input.defer="fetchSuggestions"
 *   placeholder="输入获取建议"
 * />
 *
 * <!-- 表单保存：500ms立即执行，防止重复提交 -->
 * <form v-debounce:500.submit="saveForm">
 *   <input v-model="form.name" />
 *   <button type="submit">保存</button>
 * </form>
 *
 * <!-- 窗口自适应：200ms防抖，避免频繁重绘 -->
 * <div v-debounce:200.resize="adaptLayout">
 *   自适应布局内容
 * </div>
 */

const mounted = (el, binding) => {
  // ========== 防重复挂载 ==========
  // 如果已经初始化过，直接返回
  if (el._debounceInitialized) {
    el._debounce.originalHandler = binding.value;
    return;
  }
  el._debounceInitialized = true;

  // ========== 参数解析 ==========
  // 延迟时间：v-debounce:500 中的500，默认300ms
  const delay = parseInt(binding.arg) || 300;

  // 修饰符：v-debounce.enter.defer 中的修饰符
  const modifiers = binding.modifiers || {};

  // 绑定的处理函数
  const originalHandler = binding.value;

  // 校验处理函数类型
  if (typeof originalHandler !== "function") {
    console.warn("[v-debounce] 指令需要绑定一个函数类型，收到:", typeof originalHandler);
    return;
  }

  // ========== 模式配置 ==========
  // 默认使用立即执行模式，除非指定了 .defer 修饰符
  const isImmediate = !modifiers.defer; // true: 立即执行（默认），false: 延迟执行

  // ========== 事件解析 ==========
  // 定义要防抖的事件列表
  const events = [];

  // 所有支持的事件修饰符
  const eventModifiers = [
    "click",
    "enter",
    "keyup",
    "keydown",
    "input",
    "change",
    "submit",
    "scroll",
    "resize",
    "mousemove",
    "mouseenter",
    "mouseleave"
  ];

  // 如果没有事件修饰符，默认处理点击事件
  const hasEventModifier = Object.keys(modifiers).some((key) => eventModifiers.includes(key));

  if (!hasEventModifier) {
    events.push("click");
  } else {
    // 遍历修饰符，提取事件类型
    Object.keys(modifiers).forEach((key) => {
      if (eventModifiers.includes(key)) {
        if (key === "enter" || key === "keyup") {
          events.push("keyup.enter");
        } else {
          events.push(key);
        }
      }
    });
  }

  // ========== 初始化 ==========
  el._debounce = {
    handlers: {},
    timer: null,
    events,
    originalHandler,
    delay,
    isImmediate,
    mode: isImmediate ? "immediate" : "defer"
  };

  /**
   * 防抖处理核心函数
   * @param {...any} args - 原始事件参数
   */
  const debouncedHandler = function (...args) {
    const context = this;
    const currentTimer = el._debounce.timer;

    if (el._debounce.isImmediate) {
      // 立即执行模式（默认）：第一次立即执行，后续防抖
      const callNow = !currentTimer; // 如果没有等待中的定时器，则立即执行

      // 清除已有的定时器
      if (currentTimer) {
        clearTimeout(currentTimer);
      }

      // 设置新的延迟定时器
      el._debounce.timer = setTimeout(() => {
        el._debounce.timer = null;
      }, el._debounce.delay);

      // 立即执行
      if (callNow) {
        el._debounce.originalHandler.apply(context, args);
      }
    } else {
      // 延迟执行模式：所有触发都延迟执行
      if (currentTimer) {
        clearTimeout(currentTimer);
      }

      el._debounce.timer = setTimeout(() => {
        el._debounce.originalHandler.apply(context, args);
        el._debounce.timer = null;
      }, el._debounce.delay);
    }
  };

  /**
   * 创建事件处理器
   * @param {string} eventType - 事件类型
   * @returns {Function} 事件处理函数
   */
  const createHandler = (eventType) => {
    return function (event) {
      // 特殊事件处理
      if (eventType === "keyup.enter") {
        // 只处理回车键
        const isEnter = event.key === "Enter" || event.keyCode === 13;
        if (!isEnter) return;
        event.preventDefault?.();
      } else if (eventType === "submit") {
        // 阻止表单默认提交
        event.preventDefault?.();
      }

      debouncedHandler(event);
    };
  };

  events.forEach((eventType) => {
    const baseEvent = eventType.split(".")[0];
    const handler = createHandler(eventType);

    el._debounce.handlers[eventType] = handler;

    // ===== 性能优化：根据事件类型选择合适的监听选项 =====
    let options = undefined;

    if (baseEvent === "scroll" || baseEvent === "resize" || baseEvent === "mousemove") {
      options = { passive: true };
    } else if (baseEvent === "mouseenter" || baseEvent === "mouseleave") {
      options = { capture: false };
    }

    el.addEventListener(baseEvent, handler, options);

    if (baseEvent === "submit" && el.tagName === "FORM") {
      el.addEventListener("submit", (e) => e.preventDefault(), { once: true });
    }
  });

  // 开发环境日志 - 只在首次挂载时输出
  if (process.env.NODE_ENV === "development") {
    // console.log("[v-debounce] 已挂载", {
    //   事件: events.join(", "),
    //   延迟: `${delay}ms`,
    //   模式: isImmediate ? "立即执行(默认)" : "延迟执行",
    //   元素: el.tagName.toLowerCase(),
    //   性能优化: events.some((e) => ["scroll", "resize", "mousemove"].includes(e.split(".")[0]))
    //     ? "已启用passive优化"
    //     : "标准模式"
    // });
  }
};

export const debounce = {
  mounted,

  /**
   * 指令更新钩子
   * 当绑定的函数或修饰符发生变化时，重新初始化
   */
  updated(el, binding) {
    // 检查值或修饰符是否变化
    const valueChanged = binding.value !== binding.oldValue;
    const modifiersChanged =
      JSON.stringify(binding.modifiers) !== JSON.stringify(binding.oldModifiers);

    // 如果只是函数引用变化，只更新 originalHandler
    if (valueChanged && !modifiersChanged) {
      if (el._debounce) {
        el._debounce.originalHandler = binding.value;
      }
      return;
    }

    // 如果修饰符变化，才需要重新初始化
    if (modifiersChanged) {
      // 重置初始化标记，允许重新挂载
      delete el._debounceInitialized;

      // 清理旧的事件监听
      if (el._debounce) {
        Object.entries(el._debounce.handlers || {}).forEach(([eventType, handler]) => {
          const baseEvent = eventType.split(".")[0];
          el.removeEventListener(baseEvent, handler);
        });

        if (el._debounce.timer) {
          clearTimeout(el._debounce.timer);
        }
      }

      // 重新挂载
      mounted(el, binding);
    }
  },

  /**
   * 指令卸载钩子
   * 清理事件监听和定时器，防止内存泄漏
   */
  unmounted(el) {
    // 清理初始化标记
    delete el._debounceInitialized;

    if (el._debounce) {
      // 清理事件监听
      Object.entries(el._debounce.handlers || {}).forEach(([eventType, handler]) => {
        const baseEvent = eventType.split(".")[0];
        el.removeEventListener(baseEvent, handler);
      });

      // 清理定时器
      if (el._debounce.timer) {
        clearTimeout(el._debounce.timer);
      }

      // 开发环境日志
      if (process.env.NODE_ENV === "development") {
        // console.log("[v-debounce] 已卸载", {
        //   事件: el._debounce.events?.join(", "),
        //   模式: el._debounce.mode
        // });
      }

      delete el._debounce;
    }
  }
};
